import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

function localDateISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function lineText(entry) {
  if (!entry || typeof entry !== 'object') return String(entry);
  const msg = entry.message != null ? String(entry.message) : JSON.stringify(entry);
  const ts = entry.timestamp || '';
  const lvl = (entry.level || 'info').toUpperCase();
  return ts ? `${ts} [${lvl}] ${msg}` : `[${lvl}] ${msg}`;
}

export default function Logs() {
  const [dates, setDates] = useState([]);
  const [date, setDate] = useState(localDateISO);
  const [lines, setLines] = useState(200);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const loadDates = useCallback(async () => {
    try {
      const r = await api.get('/logs/dates');
      setDates(Array.isArray(r.data.dates) ? r.data.dates : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load log dates');
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const r = await api.get('/logs', { params: { date, lines } });
      setEntries(Array.isArray(r.data.entries) ? r.data.entries : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load logs');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [date, lines]);

  useEffect(() => {
    loadDates();
  }, [loadDates]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => {
      loadLogs();
    }, 5000);
    return () => clearInterval(id);
  }, [autoRefresh, loadLogs]);

  async function handleDownload() {
    try {
      const r = await api.get('/logs/download', {
        params: { date },
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `app-${date}.log`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Download failed');
    }
  }

  return (
    <div className="page page-logs">
      <div className="page-header">
        <div>
          <h2>Server logs</h2>
          <p className="page-sub">Tail of Winston JSON logs from the API ({date}).</p>
        </div>
        <div className="header-actions logs-toolbar">
          <label className="logs-field">
            <span className="logs-field-label">Date</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              aria-label="Log date"
            />
          </label>
          {dates.length > 0 && (
            <label className="logs-field">
              <span className="logs-field-label">Available</span>
              <select value={date} onChange={(e) => setDate(e.target.value)} aria-label="Pick from available log dates">
                {dates.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="logs-field">
            <span className="logs-field-label">Lines</span>
            <select value={lines} onChange={(e) => setLines(Number(e.target.value))}>
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
            </select>
          </label>
          <label className="logs-field logs-checkbox">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <span>Auto-refresh (5s)</span>
          </label>
          <button type="button" className="btn btn-ghost" onClick={loadLogs} disabled={loading}>
            Refresh
          </button>
          <button type="button" className="btn btn-primary" onClick={handleDownload}>
            Download
          </button>
        </div>
      </div>

      {error && <div className="logs-error">{error}</div>}

      <div className="log-viewer" role="log" aria-live="polite">
        {loading && entries.length === 0 ? (
          <p className="logs-loading">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="logs-empty">No log entries for this date (or file not created yet).</p>
        ) : (
          entries.map((entry, i) => {
            const lvl = String(entry.level || 'info').toLowerCase();
            return (
              <pre key={i} className={`log-line log-level-${lvl}`}>
                {lineText(entry)}
              </pre>
            );
          })
        )}
      </div>
    </div>
  );
}
